import { create } from 'zustand';
import { ModalTypes } from '../components/ModalRenderer';
import { MODAL_CLOSE_TIMEOUT } from '../constants/constants';
import { environment } from '../utils/env.utils';

type ModalOptions = {
  payload?: unknown;
  isPersistedFromRedirect?: boolean;
  confirmAction?: () => void;
  closeAction?: () => void;
};

type ModalItem = {
  type: ModalTypes;
  options: ModalOptions;
};

type ModalState = {
  currentModal: ModalItem | null;
  nextModal: ModalItem | null;
  status: 'idle' | 'closing';

  openModal: (type: ModalTypes, options?: ModalOptions) => void;
  closeModal: () => void;
  closeNonPersistedModal: () => void;
  reset: () => void;
  advanceModal: () => void;
};

const initialState = {
  currentModal: null,
  nextModal: null,
  status: 'idle'
} satisfies Partial<ModalState>;

export const useModalStore = create<ModalState>()((set, get) => ({
  ...initialState,
  closeModal: () => {
    const { status, currentModal, advanceModal } = get();

    // If a modal is open, close it
    if (status === 'idle' && currentModal) {
      set({ status: 'closing' });

      // Animations are disabled in tests, so we don't need the timeout
      if (environment.isTesting) {
        advanceModal();
      } else {
        // Wait for the animation to finish before advancing to the next modal
        setTimeout(advanceModal, MODAL_CLOSE_TIMEOUT);
      }
    }
  },
  openModal: (type, options = {}) => {
    const modal = { type, options };
    const { status, currentModal, closeModal } = get();

    // If a modal is not open, update the current modal
    if (status === 'idle' && !currentModal) {
      set({ currentModal: modal });
      return;
    }

    set({ nextModal: modal });
    closeModal();
  },
  closeNonPersistedModal: () => {
    const { status, currentModal, closeModal } = get();

    if (status === 'idle' && !currentModal?.options.isPersistedFromRedirect) {
      closeModal();
    }
  },
  advanceModal: () => set(prev => ({ currentModal: prev.nextModal, nextModal: null, status: 'idle' })),
  reset: () => set(initialState)
}));
